<template>
  <div>
    <button class="button is-info is-small eff-upload-btn" @click="openModal">
      <span class="icon">
        <font-awesome-icon icon="upload" />
      </span>
      <span>Upload EFF File</span>
    </button>
    <div class="modal" :class="{ 'is-active': isActive }">
      <div class="modal-background" @click="closeModal"></div>
      <div class="modal-card eff-modal-card">
        <header class="modal-card-head">
          <span class="modal-card-title-icon">
            <font-awesome-icon icon="upload" />
          </span>
          <p class="modal-card-title">Upload EFF File</p>
          <button
            class="delete is-large"
            aria-label="close"
            @click="closeModal"
          ></button>
        </header>
        <section class="modal-card-body">
          <div
            class="eff-dropzone"
            :class="{
              'is-dragover': isDragOver,
              'has-file': selectedFile,
              'is-invalid': validationError,
            }"
            @dragover.prevent="isDragOver = true"
            @dragleave.prevent="isDragOver = false"
            @drop.prevent="onDrop"
            @click="$refs.fileInput.click()"
          >
            <input
              ref="fileInput"
              type="file"
              accept=".eff"
              style="display: none"
              @change="onFileChange"
            />
            <div v-if="!selectedFile" class="eff-dropzone-placeholder">
              <font-awesome-icon icon="file-upload" size="3x" color="#b5c4d4" />
              <p class="eff-dropzone-text">
                Click to select or drag &amp; drop an <strong>.eff</strong> file
                here
              </p>
              <p class="eff-dropzone-hint">
                Expected format:
                <code
                  >{AcReg}_{Carrier}_{FlightNumber}_{FlightDate:yyMMdd}_{Departure}_{Arrival}_F{Now:yyMMddHHmmss}.eff</code
                >
              </p>
            </div>
            <div v-else class="eff-dropzone-file">
              <font-awesome-icon
                icon="file-archive"
                size="2x"
                color="#3273dc"
              />
              <div class="eff-file-info">
                <p class="eff-file-name">{{ selectedFile.name }}</p>
                <p class="eff-file-size">{{ fileSizeFormatted }}</p>
              </div>
              <button
                class="delete is-medium eff-file-remove"
                title="Remove file"
                @click.stop="removeFile"
              ></button>
            </div>
          </div>
          <p v-if="validationError" class="help is-danger eff-validation-msg">
            <font-awesome-icon icon="exclamation-circle" />
            {{ validationError }}
          </p>
          <div v-if="isUploading" class="eff-progress-wrapper">
            <progress
              class="progress is-info"
              :value="uploadProgress"
              max="100"
            >
              {{ uploadProgress }}%
            </progress>
            <p class="eff-progress-label">Uploading… {{ uploadProgress }}%</p>
          </div>
          <article
            v-if="uploadResult"
            class="message"
            :class="uploadResult.type"
          >
            <div class="message-header">
              <p>{{ uploadResult.title }}</p>
            </div>
            <div class="message-body">
              <p>{{ uploadResult.text }}</p>
              <template v-if="uploadResult.details">
                <table class="table is-narrow is-bordered eff-result-table">
                  <tbody>
                    <tr v-for="(val, key) in uploadResult.details" :key="key">
                      <td>
                        <strong>{{ key }}</strong>
                      </td>
                      <td>{{ val }}</td>
                    </tr>
                  </tbody>
                </table>
              </template>
            </div>
          </article>
        </section>
        <footer class="modal-card-foot eff-modal-foot">
          <button
            class="button is-info"
            :class="{ 'is-loading': isUploading }"
            :disabled="!selectedFile || isUploading || !!validationError"
            @click="uploadFile"
          >
            <span class="icon"><font-awesome-icon icon="upload" /></span>
          </button>
        </footer>
      </div>
    </div>
  </div>
</template>

<script>
import http from "@/http";

const EFF_NAME_REGEX =
  /^[A-Z0-9]+_[A-Z]{2,3}\d+_\d{6}_[A-Z]{4}_[A-Z]{4}_F\d{12}\.eff$/i;

export default {
  name: "EffUploadModal",

  data() {
    return {
      isActive: false,
      selectedFile: null,
      isDragOver: false,
      validationError: null,
      isUploading: false,
      uploadProgress: 0,
      uploadResult: null,
    };
  },

  computed: {
    fileSizeFormatted() {
      if (!this.selectedFile) return "";
      const kb = this.selectedFile.size / 1024;
      return kb >= 1024
        ? `${(kb / 1024).toFixed(2)} MB`
        : `${kb.toFixed(1)} KB`;
    },
  },

  methods: {
    openModal() {
      this.isActive = true;
      this.resetState();
    },

    closeModal() {
      if (this.isUploading) return;
      this.isActive = false;
      this.resetState();
    },

    resetState() {
      this.selectedFile = null;
      this.validationError = null;
      this.isUploading = false;
      this.uploadProgress = 0;
      this.uploadResult = null;
      this.isDragOver = false;
    },

    onFileChange(e) {
      const file = e.target.files[0];
      if (file) this.setFile(file);
      e.target.value = "";
    },

    onDrop(e) {
      this.isDragOver = false;
      const file = e.dataTransfer.files[0];
      if (file) this.setFile(file);
    },

    setFile(file) {
      this.uploadResult = null;
      this.validationError = this.validateFileName(file.name);
      this.selectedFile = file;
    },

    removeFile() {
      this.selectedFile = null;
      this.validationError = null;
      this.uploadResult = null;
    },

    validateFileName(name) {
      if (!name.toLowerCase().endsWith(".eff")) {
        return "File must have a .eff extension.";
      }
      if (!EFF_NAME_REGEX.test(name)) {
        return `Invalid filename format. Expected: {AcReg}_{Carrier}{Flight}_{yyMMdd}_{DEP}_{ARR}_F{yyMMddHHmmss}.eff`;
      }
      return null;
    },

    async uploadFile() {
      if (!this.selectedFile || this.validationError) return;

      this.isUploading = true;
      this.uploadProgress = 0;
      this.uploadResult = null;

      const formData = new FormData();
      formData.append("file", this.selectedFile);

      try {
        const response = await http.instance.post(
          "/EffFileUpload/upload",
          formData,
          {
            headers: { "Content-Type": "multipart/form-data" },
            onUploadProgress: (progressEvent) => {
              if (progressEvent.total) {
                this.uploadProgress = Math.round(
                  (progressEvent.loaded * 100) / progressEvent.total,
                );
              }
            },
          },
        );

        const data = response.data;
        this.uploadResult = {
          type: "is-success",
          title: "Upload Successful",
          text:
            data.message || "EFF file uploaded and transferred successfully.",
          details: {
            "File Name": data.fileName,
            "AC Reg": data.acReg,
            "Carrier": data.carrier,
            "Flight No": data.flightNumber,
            "Flight Date": data.flightDate,
            "Departure": data.departure,
            "Arrival": data.arrival,
          },
        };

        this.selectedFile = null;
      } catch (err) {
        const data = err?.response?.data;
        const errMsg =
          (data && (data.error || data.title || data.message)) ||
          err?.message ||
          "An unexpected error occurred.";

        this.uploadResult = {
          type: "is-danger",
          title: "Upload Failed",
          text: errMsg,
          details: null,
        };

      } finally {
        this.isUploading = false;
        this.uploadProgress = 100;
      }
    },
  },
};
</script>

<style scoped>
.eff-upload-btn {
  font-weight: 600;
  letter-spacing: 0.03em;
}

.eff-modal-card {
  width: 750px;
  max-width: 96vw;
  border-radius: 8px;
  overflow: hidden;
}

.modal-card-head {
  background-color: #3273dc;
  gap: 10px;
  padding: 15px 20px;
}

.modal-card-head .modal-card-title {
  color: #fff;
  font-weight: 700;
}

.modal-card-title-icon {
  color: #fff;
  margin-right: 6px;
}
.modal-card-foot {
  padding: 12px 20px;
}

.eff-modal-foot {
  justify-content: flex-end;
  gap: 4px;
}

.eff-dropzone {
  border: 2px dashed #b5c4d4;
  border-radius: 8px;
  padding: 24px 18px;
  cursor: pointer;
  text-align: center;
  transition: border-color 0.2s, background-color 0.2s;
  min-height: 150px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f9fbff;
}

.eff-dropzone:hover,
.eff-dropzone.is-dragover {
  border-color: #3273dc;
  background-color: #eef4ff;
}

.eff-dropzone.has-file {
  border-style: solid;
  border-color: #3273dc;
  background-color: #f0f7ff;
}

.eff-dropzone.is-invalid {
  border-color: #f14668;
  background-color: #fff5f7;
}

.eff-dropzone-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  pointer-events: none;
}

.eff-dropzone-text {
  font-size: 0.95rem;
  color: #4a4a4a;
}

.eff-dropzone-hint {
  font-size: 0.78rem;
  color: #888;
}

.eff-dropzone-file {
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  pointer-events: none;
}

.eff-file-info {
  flex: 1;
  text-align: left;
}

.eff-file-name {
  font-weight: 600;
  font-size: 0.9rem;
  word-break: break-all;
  color: #3273dc;
}

.eff-file-size {
  font-size: 0.8rem;
  color: #888;
}

.eff-file-remove {
  pointer-events: all;
  flex-shrink: 0;
}

.eff-validation-msg {
  margin-top: 8px;
  font-size: 0.85rem;
}

.eff-progress-wrapper {
  margin-top: 16px;
}

.eff-progress-label {
  font-size: 0.8rem;
  color: #555;
  text-align: right;
  margin-top: -4px;
}

.eff-result-table {
  margin-top: 10px;
  font-size: 0.85rem;
}

.message {
  margin-top: 16px;
}
</style>
